/**
 * WeaveIn.ai Platform - Step Configuration Panel Component
 * Guided configuration UI for each workflow step type
 */

import React, { useEffect, useState, useMemo } from 'react';
import { StepType } from '@/services/api/workflows';
import { NavigationPagePicker } from './NavigationPagePicker';
import { getStepTypeOption, getStepTypeFromConfig, StepTypeOption, stepTypeDescription, stepTypeLabel } from './StepTypePicker';
import { NotifyStepConfig } from './notify/NotifyStepConfig';
import { MessageTemplateField } from './MessageTemplateField';
import { isNotifyStep } from './notify/notifySteps';
// TRUS-719: values for the template's `input` variables, saved as config.variable_inputs.
import { StepVariableInputs, variableInputsOf, withVariableInputs } from './StepVariableInputs';
import { isMemberMessageStep, messageStepTexts } from './messageStepVariables';
import { config as appConfig } from '@/src/config';
import { TemplateSelector } from '@/components/communications/TemplateSelector';
import {
  Info,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { formsAPI, FormTemplate } from '@/services/api/forms';

// ============================================================================
// CONFIGURATION FIELD TYPES
// ============================================================================

type ConfigFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'multi_select'
  | 'page_picker'
  | 'template_picker'
  | 'comm_template_picker'
  | 'duration_picker'
  | 'keyword_mapper'
  // A member-message subject / body: the org's variable picker + validation.
  | 'message_template'
  | 'message_subject';

interface SelectOption {
  value: string;
  label: string;
}

interface ConfigField {
  key: string;
  label: string;
  type: ConfigFieldType;
  description?: string;
  placeholder?: string;
  default?: any;
  options?: SelectOption[];
  showIf?: string; // Key of another field that must be truthy
  required?: boolean;
  min?: number;
  max?: number;
}

// ============================================================================
// CONFIGURATION SCHEMAS BY STEP TYPE
// ============================================================================

const CHANNEL_OPTS: SelectOption[] = [
  { value: 'sms', label: 'SMS' },
  { value: 'voice', label: 'Voice AI (IVR)' },
  { value: 'email', label: 'Email' },
  { value: 'portal', label: 'Member portal' },
];

const STEP_CONFIG_SCHEMAS: Record<string, ConfigField[]> = {
  // ---- Clinical → Social connected steps ----
  clinical_signal: [
    {
      key: 'sources',
      label: 'Initiating signals',
      type: 'multi_select',
      description: 'Which health signals may open this workflow',
      options: [
        { value: 'approved_insight', label: 'Approved AI health insight' },
        { value: 'icd_codes', label: 'EHR / claims diagnosis' },
        { value: 'observation_threshold', label: 'Observation threshold' },
      ],
      default: ['approved_insight', 'icd_codes', 'observation_threshold'],
    },
  ],
  human_approval: [
    {
      key: 'role',
      label: 'Approver role',
      type: 'select',
      description: 'Who must approve before the workflow proceeds',
      options: [
        { value: 'clinician', label: 'Clinician' },
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'care_coordinator', label: 'Care coordinator' },
      ],
      default: 'clinician',
    },
    { key: 'prompt', label: 'Approval question', type: 'textarea', placeholder: 'Approve auto-creation of social referrals from this clinical signal?' },
    {
      key: 'on_reject',
      label: 'If rejected',
      type: 'select',
      options: [
        { value: 'end', label: 'Halt the workflow' },
        { value: 'continue', label: 'Continue anyway' },
      ],
      default: 'end',
    },
  ],
  create_referral: [
    {
      key: 'source',
      label: 'Referral source',
      type: 'select',
      options: [
        { value: 'icd_to_sdoh_mapping', label: 'ICD→SDOH mapping (from the clinical signal)' },
        { value: 'manual', label: 'Manual service types' },
      ],
      default: 'icd_to_sdoh_mapping',
    },
    { key: 'multi', label: 'Create one referral per mapped social need', type: 'boolean', default: true },
    {
      key: 'urgency',
      label: 'Default urgency',
      type: 'select',
      options: [
        { value: 'routine', label: 'Routine' },
        { value: 'priority', label: 'Priority' },
        { value: 'urgent', label: 'Urgent' },
      ],
      default: 'routine',
    },
  ],
  multi_channel_escalation: [
    {
      key: 'mode',
      label: 'Path selection',
      type: 'select',
      description: 'Auto = best channel by member preference; Manual = fixed set; Escalate = try in order until a reply',
      options: [
        { value: 'auto', label: 'Auto — best channel by member preference' },
        { value: 'manual', label: 'Manual — use the selected channels' },
        { value: 'escalate', label: 'Escalate — try each in order until a reply' },
      ],
      default: 'auto',
    },
    {
      key: 'channels',
      label: 'Channels',
      type: 'multi_select',
      description: 'Communication channels this step may use',
      options: CHANNEL_OPTS,
      default: ['sms', 'voice', 'email'],
    },
    { key: 'escalate_after_hours', label: 'Escalate after (hours)', type: 'number', default: 24, min: 1, max: 168 },
  ],
  notify_pcp: [
    {
      key: 'channel',
      label: 'Channel',
      type: 'select',
      options: [
        { value: 'portal', label: 'Member portal / EHR note' },
        { value: 'email', label: 'Email' },
        { value: 'fhir', label: 'FHIR write-back' },
      ],
      default: 'portal',
    },
    { key: 'include_outcome', label: 'Include the service outcome in the loop-back', type: 'boolean', default: true },
  ],
  ai_agent: [
    {
      key: 'agent_key',
      label: 'Agent',
      type: 'select',
      description: 'Authored LLM agent invoked for a grounded decision (list from your org)',
      options: [], // injected live from the step catalog
    },
    { key: 'decision_prompt', label: 'Extra instruction (optional)', type: 'textarea', placeholder: 'Appended to the agent prompt for this step' },
    { key: 'decline_halts', label: 'Halt the workflow if the agent decides to decline', type: 'boolean', default: true },
  ],
  provider_match: [
    {
      key: 'mode',
      label: 'Matching mode',
      type: 'select',
      description: 'How providers are found for the service',
      options: [
        { value: 'auto', label: 'Auto — AI ranks & selects the best match' },
        { value: 'manual', label: 'Manual — staff selects from suggestions' },
        { value: 'network_only', label: 'Network only — contracted providers' },
        { value: 'cbo_only', label: 'CBO only — community organizations' },
      ],
      default: 'auto',
    },
    { key: 'max_providers', label: 'Max providers to suggest', type: 'number', default: 5, min: 1, max: 20 },
    { key: 'prefer_network', label: 'Prefer contracted network providers', type: 'boolean', default: true },
    { key: 'max_distance_miles', label: 'Max distance (miles)', type: 'number', default: 25, min: 1, max: 200 },
    { key: 'require_review', label: 'Require staff review before booking', type: 'boolean', default: false },
  ],
  // Communication - SMS
  communication_sms: [
    {
      key: 'comm_template_id',
      label: 'Use Template (Optional)',
      type: 'comm_template_picker',
      description: 'Select a communication template to pre-fill the message, or write a custom message below',
    },
    {
      key: 'message_body',
      label: 'Message',
      type: 'message_template',
      required: true,
      description: 'Loading a template pre-fills this field. Edit freely.',
    },
    {
      key: 'form_template_id',
      label: 'Attach Form (Optional)',
      type: 'template_picker',
      description: 'Attach a form or assessment the member needs to complete in this step',
    },
    {
      key: 'expects_response',
      label: 'Wait for Reply',
      type: 'boolean',
      default: false,
      description: 'Pause workflow until member responds or completes the attached form',
    },
    {
      key: 'timeout_hours',
      label: 'Response Timeout (hours)',
      type: 'number',
      default: 24,
      min: 1,
      max: 168,
      showIf: 'expects_response',
      description: 'How long to wait before triggering a reminder',
    },
    {
      key: 'reminder_enabled',
      label: 'Enable Reminders',
      type: 'boolean',
      default: true,
      showIf: 'expects_response',
      description: 'Send reminders if the member has not responded or completed the form',
    },
    {
      key: 'reminder_type',
      label: 'Reminder Type',
      type: 'select',
      options: [
        { value: 'scheduled', label: 'Scheduled Interval' },
        { value: 'ai_powered', label: 'AI-Optimized Timing' },
      ],
      default: 'scheduled',
      showIf: 'reminder_enabled',
      description: 'Scheduled sends at fixed intervals; AI optimizes timing based on member engagement',
    },
    {
      key: 'reminder_interval_hours',
      label: 'Reminder Interval (hours)',
      type: 'number',
      default: 24,
      min: 4,
      max: 168,
      showIf: 'reminder_enabled',
      description: 'Hours between each reminder (scheduled) or max interval (AI)',
    },
    {
      key: 'max_reminders',
      label: 'Max Reminders',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      showIf: 'reminder_enabled',
      description: 'Maximum number of reminders before escalation',
    },
    {
      key: 'navigation_url',
      label: 'Navigate To (Optional)',
      type: 'page_picker',
      description: 'Page to open when starting this step',
    },
  ],

  // Communication - Email
  communication_email: [
    {
      key: 'comm_template_id',
      label: 'Use Template (Optional)',
      type: 'comm_template_picker',
      description: 'Select a communication template to pre-fill subject and body',
    },
    {
      key: 'subject',
      label: 'Subject Line',
      type: 'message_subject',
      required: true,
      placeholder: 'Your care plan update',
      description: 'Loading a template pre-fills this.',
    },
    {
      key: 'message_body',
      label: 'Email Body',
      type: 'message_template',
      required: true,
      description: 'Loading a template pre-fills this. Edit freely.',
    },
    {
      key: 'form_template_id',
      label: 'Attach Form (Optional)',
      type: 'template_picker',
      description: 'Attach a form or assessment the member needs to complete',
    },
    {
      key: 'expects_response',
      label: 'Track Opens/Clicks',
      type: 'boolean',
      default: true,
      description: 'Track when member opens or clicks links',
    },
    {
      key: 'reminder_enabled',
      label: 'Enable Reminders',
      type: 'boolean',
      default: true,
      description: 'Send follow-up reminders if form is not completed',
    },
    {
      key: 'reminder_type',
      label: 'Reminder Type',
      type: 'select',
      options: [
        { value: 'scheduled', label: 'Scheduled Interval' },
        { value: 'ai_powered', label: 'AI-Optimized Timing' },
      ],
      default: 'scheduled',
      showIf: 'reminder_enabled',
      description: 'Scheduled sends at fixed intervals; AI optimizes based on engagement',
    },
    {
      key: 'reminder_interval_hours',
      label: 'Reminder Interval (hours)',
      type: 'number',
      default: 48,
      min: 4,
      max: 168,
      showIf: 'reminder_enabled',
    },
    {
      key: 'max_reminders',
      label: 'Max Reminders',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      showIf: 'reminder_enabled',
    },
    {
      key: 'navigation_url',
      label: 'Navigate To (Optional)',
      type: 'page_picker',
      description: 'Page to open when starting this step',
    },
  ],

  // Communication - Phone (Manual)
  communication_phone: [
    {
      key: 'call_script',
      label: 'Call Script/Notes',
      type: 'textarea',
      placeholder: 'Key points to cover during the call...',
      description: 'Guidance for the staff member making the call',
    },
    {
      key: 'form_template_id',
      label: 'Attach Form (Optional)',
      type: 'template_picker',
      description: 'Form for the member to complete during or after the call',
    },
    {
      key: 'expected_duration',
      label: 'Expected Duration (minutes)',
      type: 'number',
      default: 15,
      min: 5,
      max: 120,
    },
    {
      key: 'requires_notes',
      label: 'Require Call Notes',
      type: 'boolean',
      default: true,
      description: 'Staff must enter notes after the call',
    },
    {
      key: 'reminder_enabled',
      label: 'Enable Follow-up Reminder',
      type: 'boolean',
      default: false,
      description: 'Send a reminder if attached form is not completed after the call',
    },
    {
      key: 'reminder_interval_hours',
      label: 'Reminder After (hours)',
      type: 'number',
      default: 24,
      min: 4,
      max: 168,
      showIf: 'reminder_enabled',
    },
    {
      key: 'navigation_url',
      label: 'Navigate To',
      type: 'page_picker',
      description: 'Page to open when starting this call',
    },
  ],

  // Communication - Voice AI
  communication_voice: [
    {
      key: 'comm_template_id',
      label: 'Use Script Template (Optional)',
      type: 'comm_template_picker',
      description: 'Select a voice script template for the AI agent conversation',
    },
    {
      key: 'voice_agent_id',
      label: 'Voice Agent',
      type: 'select',
      options: [
        { value: 'outreach_general', label: 'General Outreach Agent' },
        { value: 'appointment_reminder', label: 'Appointment Reminder Agent' },
        { value: 'survey_collector', label: 'Survey Collection Agent' },
        { value: 'health_check', label: 'Health Check-in Agent' },
      ],
      description: 'AI voice agent to handle the call',
    },
    {
      key: 'form_template_id',
      label: 'Attach Form (Optional)',
      type: 'template_picker',
      description: 'Form to send to the member after the AI call',
    },
    {
      key: 'max_attempts',
      label: 'Max Call Attempts',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      description: 'Number of times to retry if no answer',
    },
    {
      key: 'retry_interval_hours',
      label: 'Retry Interval (hours)',
      type: 'number',
      default: 4,
      min: 1,
      max: 48,
      description: 'Time between retry attempts',
    },
    {
      key: 'reminder_enabled',
      label: 'Enable Form Reminder',
      type: 'boolean',
      default: true,
      description: 'Send reminders if attached form is not completed',
    },
    {
      key: 'reminder_type',
      label: 'Reminder Type',
      type: 'select',
      options: [
        { value: 'scheduled', label: 'Scheduled Interval' },
        { value: 'ai_powered', label: 'AI-Optimized Timing' },
      ],
      default: 'ai_powered',
      showIf: 'reminder_enabled',
    },
    {
      key: 'max_reminders',
      label: 'Max Reminders',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      showIf: 'reminder_enabled',
    },
  ],

  // Form Step
  form: [
    {
      key: 'form_template_id',
      label: 'Form Template',
      type: 'template_picker',
      required: true,
      description: 'Select the form to assign',
    },
    {
      key: 'form_type',
      label: 'Form Type',
      type: 'select',
      options: [
        { value: 'intake_assessment', label: 'Intake Assessment' },
        { value: 'care_plan', label: 'Care Plan' },
        { value: 'consent', label: 'Consent Form' },
        { value: 'progress_note', label: 'Progress Note' },
        { value: 'eligibility', label: 'Eligibility Verification' },
        { value: 'custom', label: 'Custom Form' },
      ],
      default: 'custom',
    },
    {
      key: 'requires_signature',
      label: 'Requires E-Signature',
      type: 'boolean',
      default: false,
      description: 'Member must sign the form electronically',
    },
    {
      key: 'blocks_workflow',
      label: 'Must Complete Before Next Step',
      type: 'boolean',
      default: true,
      description: 'Workflow cannot proceed until form is submitted',
    },
    {
      key: 'due_offset_days',
      label: 'Due In (Days)',
      type: 'number',
      default: 7,
      min: 1,
      max: 90,
      description: 'Days from step start until form is due',
    },
    {
      key: 'navigation_url',
      label: 'Navigate To',
      type: 'page_picker',
      description: 'Page where the form can be completed',
    },
  ],

  // Task Step
  task: [
    {
      key: 'description',
      label: 'Task Description',
      type: 'textarea',
      required: true,
      placeholder: 'Describe the action the staff member needs to take...',
      description: 'Clear instructions for completing this task',
    },
    {
      key: 'task_type',
      label: 'Task Type',
      type: 'select',
      options: [
        { value: 'general', label: 'General Task' },
        { value: 'document_upload', label: 'Document Upload' },
        { value: 'data_entry', label: 'Data Entry' },
        { value: 'review', label: 'Review/Verification' },
        { value: 'follow_up', label: 'Follow-up Required' },
      ],
      default: 'general',
    },
    {
      key: 'estimated_minutes',
      label: 'Estimated Duration (minutes)',
      type: 'number',
      default: 15,
      min: 5,
      max: 480,
    },
    {
      key: 'navigation_url',
      label: 'Navigate To',
      type: 'page_picker',
      description: 'Page to open when starting this task',
    },
  ],

  // Document Step
  document: [
    {
      key: 'required_documents',
      label: 'Required Documents',
      type: 'multi_select',
      options: [
        { value: 'hipaa_consent', label: 'HIPAA Consent' },
        { value: 'treatment_consent', label: 'Treatment Consent' },
        { value: 'telehealth_consent', label: 'Telehealth Consent' },
        { value: 'release_of_information', label: 'Release of Information' },
        { value: 'care_plan', label: 'Care Plan' },
        { value: 'pcp_signature', label: 'PCP Signature' },
        { value: 'id_document', label: 'ID Document' },
        { value: 'insurance_card', label: 'Insurance Card' },
      ],
      description: 'Select all document types required',
    },
    {
      key: 'requires_e_signature',
      label: 'Requires E-Signature',
      type: 'boolean',
      default: false,
    },
    {
      key: 'navigation_url',
      label: 'Navigate To',
      type: 'page_picker',
      description: 'Page for document upload',
    },
  ],

  // Assessment Step
  assessment: [
    {
      key: 'form_template_id',
      label: 'Assessment Form',
      type: 'template_picker',
      required: true,
      description: 'Select an assessment form from the form library',
    },
    {
      key: 'auto_score',
      label: 'Auto-Calculate Score',
      type: 'boolean',
      default: true,
      description: 'Automatically calculate assessment score',
    },
    {
      key: 'blocks_workflow',
      label: 'Blocks Workflow Progress',
      type: 'boolean',
      default: true,
      description: 'Workflow cannot proceed until assessment is completed',
    },
    {
      key: 'navigation_url',
      label: 'Navigate To',
      type: 'page_picker',
    },
  ],

  // Wait Step
  wait: [
    {
      key: 'duration_hours',
      label: 'Wait Duration',
      type: 'duration_picker',
      required: true,
      description: 'How long to wait before proceeding',
    },
    {
      key: 'resume_condition',
      label: 'Resume When',
      type: 'select',
      options: [
        { value: 'time_elapsed', label: 'Time Elapsed (24/7)' },
        { value: 'business_hours_only', label: 'Business Hours Only (M-F 9-5)' },
        { value: 'specific_time', label: 'Specific Time of Day' },
      ],
      default: 'time_elapsed',
    },
    {
      key: 'resume_time',
      label: 'Resume At Time',
      type: 'text',
      placeholder: '09:00',
      showIf: 'resume_condition',
      description: 'Time to resume (24h format)',
    },
  ],

  // Wait for Response Step
  wait_for_response: [
    {
      key: 'required_channels',
      label: 'Expected Response Via',
      type: 'multi_select',
      options: [
        { value: 'SMS', label: 'SMS' },
        { value: 'EMAIL', label: 'Email' },
        { value: 'PHONE', label: 'Phone Call' },
        { value: 'PORTAL', label: 'Member Portal' },
      ],
      default: ['SMS'],
      description: 'Channels the member can respond through',
    },
    {
      key: 'timeout_hours',
      label: 'Timeout (hours)',
      type: 'number',
      default: 24,
      min: 1,
      max: 168,
      description: 'How long to wait for a response',
    },
    {
      key: 'timeout_action',
      label: 'On Timeout',
      type: 'select',
      options: [
        { value: 'escalate', label: 'Escalate to Supervisor' },
        { value: 'skip', label: 'Skip to Next Step' },
        { value: 'retry', label: 'Retry Communication' },
        { value: 'notify', label: 'Notify Case Manager' },
      ],
      default: 'escalate',
    },
    {
      key: 'response_keywords',
      label: 'Response Keywords',
      type: 'keyword_mapper',
      description: 'Map response keywords to actions',
    },
  ],

  // Approval Step
  approval: [
    {
      key: 'approver_role',
      label: 'Approver Role',
      type: 'select',
      required: true,
      options: [
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'pcp', label: 'Primary Care Provider (PCP)' },
        { value: 'care_manager', label: 'Care Manager' },
        { value: 'medical_director', label: 'Medical Director' },
        { value: 'billing_manager', label: 'Billing Manager' },
      ],
      default: 'supervisor',
    },
    {
      key: 'requires_signature',
      label: 'Requires Signature',
      type: 'boolean',
      default: false,
    },
    {
      key: 'timeout_hours',
      label: 'Auto-Escalate After (hours)',
      type: 'number',
      default: 48,
      min: 4,
      max: 168,
      description: 'Escalate if not approved within this time',
    },
    {
      key: 'escalation_role',
      label: 'Escalate To',
      type: 'select',
      options: [
        { value: 'supervisor', label: 'Next Level Supervisor' },
        { value: 'medical_director', label: 'Medical Director' },
        { value: 'admin', label: 'Administrator' },
      ],
      default: 'supervisor',
    },
    {
      key: 'navigation_url',
      label: 'Review Page',
      type: 'page_picker',
      description: 'Page where approver reviews the item',
    },
  ],

  // Billing Step
  billing: [
    {
      key: 'billing_type',
      label: 'Billing Type',
      type: 'select',
      required: true,
      options: [
        { value: 'service', label: 'Service - Billable Service Performed' },
        { value: 'enrollment', label: 'Enrollment - Initial Assessment/Intake' },
        { value: 'assessment', label: 'Assessment - Comprehensive Assessment' },
        { value: 'care_plan', label: 'Care Plan - Care Plan Development' },
        { value: 'outreach', label: 'Outreach - Member Outreach Activity' },
      ],
      default: 'service',
    },
    {
      key: 'place_of_service',
      label: 'Place of Service',
      type: 'select',
      options: [
        { value: '02', label: '02 - Telehealth' },
        { value: '11', label: '11 - Office' },
        { value: '12', label: '12 - Home' },
        { value: '99', label: '99 - Other' },
      ],
      default: '02',
    },
    {
      key: 'auto_populate',
      label: 'Auto-populate from Enrollment',
      type: 'boolean',
      default: true,
      description: 'Pre-fill billing info from enrollment data',
    },
    {
      key: 'save_as_draft',
      label: 'Save as Draft',
      type: 'boolean',
      default: true,
      description: 'Requires review before submission',
    },
  ],

  // API Call Step
  api_call: [
    {
      key: 'api_endpoint',
      label: 'API Endpoint',
      type: 'select',
      options: [
        { value: 'eligibility_check', label: 'Eligibility Verification' },
        { value: 'pharmacy_sync', label: 'Pharmacy Sync' },
        { value: 'ehr_update', label: 'EHR Update' },
        { value: 'notification_service', label: 'Notification Service' },
        { value: 'custom', label: 'Custom Endpoint' },
      ],
      default: 'eligibility_check',
    },
    {
      key: 'custom_url',
      label: 'Custom URL',
      type: 'text',
      placeholder: 'https://api.example.com/endpoint',
      showIf: 'api_endpoint',
    },
    {
      key: 'retry_on_failure',
      label: 'Retry on Failure',
      type: 'boolean',
      default: true,
    },
    {
      key: 'max_retries',
      label: 'Max Retries',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      showIf: 'retry_on_failure',
    },
  ],

  // Notification Step
  notification: [
    {
      key: 'notification_type',
      label: 'Notification Type',
      type: 'select',
      options: [
        { value: 'in_app', label: 'In-App Notification' },
        { value: 'email_staff', label: 'Email to Staff' },
        { value: 'slack', label: 'Slack Message' },
        { value: 'sms_staff', label: 'SMS to Staff' },
      ],
      default: 'in_app',
    },
    {
      key: 'recipient_role',
      label: 'Notify',
      type: 'select',
      options: [
        { value: 'assigned_user', label: 'Assigned User' },
        { value: 'care_team', label: 'Entire Care Team' },
        { value: 'supervisor', label: 'Supervisor' },
        { value: 'specific_user', label: 'Specific User' },
      ],
      default: 'assigned_user',
    },
    {
      key: 'message',
      label: 'Notification Message',
      type: 'textarea',
      placeholder: 'A new task requires your attention...',
    },
  ],

  // Pricing Agent Step
  pricing_agent: [
    {
      key: 'channel',
      label: 'Negotiation Channel',
      type: 'select',
      options: [
        { value: 'email', label: 'Email' },
        { value: 'sms', label: 'SMS' },
        { value: 'voice', label: 'Voice AI' },
        { value: 'portal', label: 'Provider Portal' },
      ],
      default: 'email',
    },
    {
      key: 'strategy',
      label: 'Negotiation Strategy',
      type: 'select',
      options: [
        { value: 'balanced', label: 'Balanced - Fair Market Rate' },
        { value: 'aggressive', label: 'Aggressive - Lower Offers' },
        { value: 'accommodating', label: 'Accommodating - Quick Agreement' },
        { value: 'urgent', label: 'Urgent - Fast Resolution' },
      ],
      default: 'balanced',
    },
    {
      key: 'max_rounds',
      label: 'Max Negotiation Rounds',
      type: 'select',
      options: [
        { value: '3', label: '3 rounds' },
        { value: '5', label: '5 rounds (recommended)' },
        { value: '7', label: '7 rounds' },
        { value: '10', label: '10 rounds' },
      ],
      default: '5',
    },
    {
      key: 'auto_initiate',
      label: 'Auto-initiate When Provider Selected',
      type: 'boolean',
      default: true,
    },
    {
      key: 'escalate_on_timeout',
      label: 'Escalate to Human if No Agreement',
      type: 'boolean',
      default: true,
    },
    {
      key: 'timeout_hours',
      label: 'Response Timeout (hours)',
      type: 'number',
      default: 48,
      min: 1,
      max: 168,
      description: 'Time to wait for provider response between rounds',
    },
  ],

  // Condition/Branching Step
  condition: [
    {
      key: 'branching_mode',
      label: 'Branching Mode',
      type: 'select',
      required: true,
      options: [
        { value: 'rule_based', label: 'Rule-Based' },
        { value: 'ai_decision', label: 'AI-Powered Decision' },
      ],
      default: 'rule_based',
      description: 'Choose rule-based conditions or AI-powered routing',
    },
    {
      key: 'condition_field',
      label: 'Field to Evaluate',
      type: 'text',
      placeholder: 'enrollment.status or form.score or response.action',
      showIf: 'branching_mode',
      description: 'The field value that determines the routing path',
    },
    {
      key: 'condition_operator',
      label: 'Operator',
      type: 'select',
      options: [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Does Not Equal' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'contains', label: 'Contains' },
        { value: 'is_not_empty', label: 'Has Value' },
        { value: 'is_empty', label: 'Is Empty' },
      ],
      default: 'equals',
      showIf: 'branching_mode',
      description: 'Comparison operator for rule-based evaluation',
    },
    {
      key: 'condition_value',
      label: 'Expected Value',
      type: 'text',
      placeholder: 'active, completed, >80, etc.',
      showIf: 'branching_mode',
      description: 'The value to compare against',
    },
    {
      key: 'decision_context',
      label: 'AI Decision Context',
      type: 'textarea',
      placeholder: 'Evaluate the member\'s care plan progress, engagement, and assessment results to determine routing...',
      showIf: 'branching_mode',
      description: 'Describe what the AI should evaluate (only used in AI mode)',
    },
    {
      key: 'confidence_threshold',
      label: 'AI Confidence Threshold (%)',
      type: 'number',
      default: 70,
      min: 50,
      max: 99,
      showIf: 'branching_mode',
      description: 'Minimum confidence to auto-route. Below this, escalate to staff.',
    },
    {
      key: 'true_branch_step',
      label: 'If True / Primary Path — Go To Step',
      type: 'number',
      description: 'Step number to proceed to when condition is met or AI recommends primary action',
    },
    {
      key: 'false_branch_step',
      label: 'If False / Alternate Path — Go To Step',
      type: 'number',
      description: 'Step number to proceed to when condition is not met or AI recommends alternate',
    },
  ],

  // Campaign Response Step
  campaign_response: [
    {
      key: 'campaign_id',
      label: 'Campaign',
      type: 'select',
      options: [
        { value: 'active', label: 'Active Campaign' },
        { value: 'specific', label: 'Specific Campaign ID' },
      ],
      default: 'active',
    },
    {
      key: 'response_type',
      label: 'Expected Response Type',
      type: 'multi_select',
      options: [
        { value: 'opt_in', label: 'Opt-In' },
        { value: 'opt_out', label: 'Opt-Out' },
        { value: 'schedule', label: 'Schedule Request' },
        { value: 'info_request', label: 'Information Request' },
      ],
      default: ['opt_in'],
    },
    {
      key: 'timeout_hours',
      label: 'Timeout (hours)',
      type: 'number',
      default: 72,
      min: 1,
      max: 336,
    },
  ],

  // ML Intelligence - Prediction Gate
  prediction_gate: [
    {
      key: 'prediction_type',
      label: 'Prediction Type',
      type: 'select',
      required: true,
      options: [
        { value: 'churn', label: 'Churn Risk' },
        { value: 'engagement_decline', label: 'Engagement Decline' },
        { value: 'combined', label: 'Combined Risk Score' },
      ],
      default: 'churn',
      description: 'Type of ML prediction to evaluate for this member',
    },
    {
      key: 'horizon',
      label: 'Prediction Horizon',
      type: 'select',
      required: true,
      options: [
        { value: '7d', label: '7 Days' },
        { value: '14d', label: '14 Days' },
        { value: '30d', label: '30 Days' },
      ],
      default: '30d',
      description: 'How far ahead to predict (shorter = more urgent, less certain)',
    },
    {
      key: 'high_risk_threshold',
      label: 'High Risk Threshold',
      type: 'number',
      default: 0.7,
      min: 0.1,
      max: 1.0,
      description: 'Score >= this routes to high risk path (0.0-1.0)',
    },
    {
      key: 'moderate_risk_threshold',
      label: 'Moderate Risk Threshold',
      type: 'number',
      default: 0.4,
      min: 0.1,
      max: 1.0,
      description: 'Score >= this (but < high) routes to moderate risk path',
    },
    {
      key: 'high_risk_route',
      label: 'High Risk Route Label',
      type: 'text',
      default: 'high_risk_path',
      description: 'Branch name for high-risk members',
    },
    {
      key: 'moderate_risk_route',
      label: 'Moderate Risk Route Label',
      type: 'text',
      default: 'moderate_risk_path',
      description: 'Branch name for moderate-risk members',
    },
    {
      key: 'low_risk_route',
      label: 'Low Risk Route Label',
      type: 'text',
      default: 'low_risk_path',
      description: 'Branch name for low-risk members (default fallback)',
    },
    {
      key: 'store_result',
      label: 'Store Prediction Result',
      type: 'boolean',
      default: true,
      description: 'Save the prediction score on the workflow instance for audit',
    },
  ],

  // ML Intelligence - Recommendation Action
  recommendation_action: [
    {
      key: 'top_n',
      label: 'Top N Recommendations',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      description: 'Number of top recommendations to consider',
    },
    {
      key: 'min_confidence',
      label: 'Minimum Confidence',
      type: 'number',
      default: 0.6,
      min: 0.1,
      max: 1.0,
      description: 'Only include recommendations with confidence above this threshold',
    },
    {
      key: 'action',
      label: 'Action Mode',
      type: 'select',
      required: true,
      options: [
        { value: 'present_to_case_manager', label: 'Create Task for Case Manager' },
        { value: 'auto_enroll', label: 'Auto-Enroll (High Confidence Only)' },
      ],
      default: 'present_to_case_manager',
      description: 'What to do with the recommendations',
    },
    {
      key: 'auto_enroll_threshold',
      label: 'Auto-Enroll Confidence Threshold',
      type: 'number',
      default: 0.85,
      min: 0.5,
      max: 1.0,
      showIf: 'action',
      description: 'Only auto-enroll when confidence exceeds this (applies to auto-enroll mode)',
    },
    {
      key: 'filter_reasons',
      label: 'Filter by Reason',
      type: 'multi_select',
      options: [
        { value: 'health_indicator', label: 'Health Indicators' },
        { value: 'sdoh_factor', label: 'SDOH Factors' },
        { value: 'profile_match', label: 'Profile Match' },
        { value: 're_engagement', label: 'Re-Engagement' },
        { value: 'similar_employees', label: 'Similar Members' },
        { value: 'employer_priority', label: 'Employer Priority' },
      ],
      description: 'Only include recommendations matching these reasons (leave empty for all)',
    },
  ],

  // External Data Ingest Step (Integration Workbench)
  external_data_ingest: [
    {
      key: 'source_type',
      label: 'Data Source',
      type: 'select',
      required: true,
      options: [
        { value: 'integration_workbench', label: 'Integration Workbench (File Upload)' },
        { value: 'mif_file', label: 'MIF File (MCP Referral)' },
        { value: 'hl7_message', label: 'HL7 Message (ADT/ORM)' },
        { value: 'csv_import', label: 'CSV/Excel Import' },
        { value: 'api_webhook', label: 'API Webhook' },
        { value: 'sftp_pickup', label: 'SFTP File Pickup' },
      ],
      default: 'mif_file',
      description: 'Where the external data comes from',
    },
    {
      key: 'expected_entity_type',
      label: 'Expected Entity Type',
      type: 'select',
      options: [
        { value: 'contact', label: 'Contact (Pre-Member)' },
        { value: 'member', label: 'Member' },
        { value: 'referral', label: 'Referral' },
      ],
      default: 'contact',
      description: 'What type of record this data creates or updates',
    },
    {
      key: 'wait_for_data',
      label: 'Wait for Data',
      type: 'boolean',
      default: true,
      description: 'Block workflow until data is received',
    },
    {
      key: 'auto_map_fields',
      label: 'Auto-Map Fields',
      type: 'boolean',
      default: true,
      description: 'Automatically map incoming fields to contact/member fields',
    },
    {
      key: 'duplicate_check',
      label: 'Run Duplicate Check',
      type: 'boolean',
      default: true,
      description: 'Check for duplicates before creating new records',
    },
    {
      key: 'mcp_source',
      label: 'MCP Source (Optional)',
      type: 'text',
      placeholder: 'e.g., Anthem, Health Net, Molina',
      description: 'Managed Care Plan sending the data',
    },
  ],

  // Navigate to Contact Step
  navigate_contact: [
    {
      key: 'target_tab',
      label: 'Target Tab',
      type: 'select',
      required: true,
      options: [
        { value: 'overview', label: 'Contact Overview' },
        { value: 'activities', label: 'Activities & Workflows' },
        { value: 'documents', label: 'Documents' },
        { value: 'notes', label: 'Case Notes' },
        { value: 'goals', label: 'Goals' },
        { value: 'tracking', label: 'Task Tracking' },
        { value: 'conversations', label: 'Conversations' },
        { value: 'forms', label: 'Forms' },
      ],
      default: 'overview',
      description: 'Which tab to open on the contact detail page',
    },
    {
      key: 'action_prompt',
      label: 'Action Prompt',
      type: 'text',
      placeholder: 'e.g., Review uploaded MIF and verify eligibility',
      description: 'Instructions shown to staff when navigating to the contact',
    },
  ],
};

// ============================================================================
// TEMPLATE PICKER SUB-COMPONENT
// ============================================================================

const TemplatePicker: React.FC<{
  value: number | null;
  onChange: (id: number | null) => void;
}> = ({ value, onChange }) => {
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    formsAPI
      .listFormTemplates({ is_active: true })
      .then((data) => {
        if (!cancelled) setTemplates(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setTemplates([]);
          setError(err?.response?.data?.detail || 'Failed to load form templates');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-400 bg-gray-50">
        Loading templates...
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 py-2 border border-red-300 rounded-md text-sm text-red-600 bg-red-50">
        {error}
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="px-3 py-2 border border-amber-300 rounded-md text-sm text-amber-700 bg-amber-50">
        No form templates found. Create templates in the Form Library first.
      </div>
    );
  }

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="">Select a form template...</option>
      {templates.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name} {t.form_type ? `(${t.form_type})` : ''}
        </option>
      ))}
    </select>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

interface StepConfigurationPanelProps {
  stepTypeValue: string;
  baseType: StepType;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
  className?: string;
  /**
   * TRUS-686: the workflow is about a member, so "Post to channel" may only
   * pick private channels. Defaults to true (the safe choice) when unknown.
   */
  memberBound?: boolean;
}

export const StepConfigurationPanel: React.FC<StepConfigurationPanelProps> = ({
  stepTypeValue,
  baseType,
  config,
  onChange,
  className = '',
  memberBound = true,
}) => {
  const { t } = useAppTranslation();
  const [showAdvanced, setShowAdvanced] = useState(true);

  // Fetch dynamic doc types from admin configuration
  const [dynamicDocTypes, setDynamicDocTypes] = useState<Array<{value: string; label: string}>>([]);
  const [dynamicAgents, setDynamicAgents] = useState<SelectOption[]>([]);
  const [dynamicChannels, setDynamicChannels] = useState<SelectOption[]>([]);

  // Live agent + channel lists for the connected steps (ai_agent / multi-channel),
  // so an admin can pick a real agent or channel path (manual or auto).
  useEffect(() => {
    if (stepTypeValue !== 'ai_agent' && stepTypeValue !== 'multi_channel_escalation') return;
    let alive = true;
    import('@/services/api/tcoServiceReferrals')
      .then(({ tcoWorkflowsAPI }) => tcoWorkflowsAPI.getStepCatalog())
      .then((cat) => {
        if (!alive) return;
        setDynamicAgents((cat.agents || []).map((a) => ({ value: a.key, label: a.label || a.key })));
        setDynamicChannels((cat.channels || []).map((c) => ({ value: c.key, label: c.label })));
      })
      .catch(() => {
        /* degrade to static options */
      });
    return () => {
      alive = false;
    };
  }, [stepTypeValue]);

  useEffect(() => {
    if (baseType === 'document' || stepTypeValue === 'document' || stepTypeValue === 'upload_document') {
      const fetchDocTypes = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const cboId = localStorage.getItem('active_cbo_id');
          const res = await fetch(`${appConfig.weaveinApi.baseUrl}/api/doc-types?include_inactive=false`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': cboId || '',
            },
          });
          if (res.ok) {
            const data = await res.json();
            setDynamicDocTypes(data.map((dt: any) => ({
              value: dt.name,
              label: `${dt.name} (${dt.category_name})`,
            })));
          }
        } catch (e) {
          console.warn('Could not fetch doc types:', e);
        }
      };
      fetchDocTypes();
    }
  }, [baseType, stepTypeValue]);

  // Build schema with dynamic doc types injected
  const schema = useMemo(() => {
    const baseSchema = STEP_CONFIG_SCHEMAS[stepTypeValue] || STEP_CONFIG_SCHEMAS[baseType] || [];

    // If this is a document step and we have dynamic doc types, replace the hardcoded options
    if (dynamicDocTypes.length > 0 && (baseType === 'document' || stepTypeValue === 'document' || stepTypeValue === 'upload_document')) {
      return baseSchema.map((field: ConfigField) => {
        if (field.key === 'required_documents' && field.type === 'multi_select') {
          return {
            ...field,
            options: dynamicDocTypes,
            description: 'Select document types required (from Document Management configuration)',
          };
        }
        return field;
      });
    }

    // Inject the live agent list into the ai_agent step's agent picker.
    if (stepTypeValue === 'ai_agent' && dynamicAgents.length > 0) {
      return baseSchema.map((field: ConfigField) =>
        field.key === 'agent_key' ? { ...field, options: dynamicAgents } : field
      );
    }
    // Inject the live channel list into the multi-channel step.
    if (stepTypeValue === 'multi_channel_escalation' && dynamicChannels.length > 0) {
      return baseSchema.map((field: ConfigField) =>
        field.key === 'channels' ? { ...field, options: dynamicChannels } : field
      );
    }

    return baseSchema;
  }, [stepTypeValue, baseType, dynamicDocTypes, dynamicAgents, dynamicChannels]);

  // Get step type info for display
  const stepTypeInfo = getStepTypeOption(stepTypeValue) || getStepTypeFromConfig(baseType, config);

  // Handle field value change
  const handleFieldChange = (key: string, value: any) => {
    onChange({
      ...config,
      [key]: value,
    });
  };

  // Check if a field should be shown based on showIf condition
  const shouldShowField = (field: ConfigField): boolean => {
    if (!field.showIf) return true;

    const dependsOnValue = config[field.showIf];

    // Branching mode: show rule-based fields only in rule_based mode,
    // AI fields only in ai_decision mode
    if (field.showIf === 'branching_mode') {
      const mode = config['branching_mode'] || 'rule_based';
      if (['condition_field', 'condition_operator', 'condition_value'].includes(field.key)) {
        return mode === 'rule_based';
      }
      if (['decision_context', 'confidence_threshold'].includes(field.key)) {
        return mode === 'ai_decision';
      }
      return true;
    }
    // Legacy condition_type support
    if (field.showIf === 'condition_type' && field.key === 'condition_field') {
      return dependsOnValue === 'field_value';
    }
    if (field.showIf === 'api_endpoint' && field.key === 'custom_url') {
      return dependsOnValue === 'custom';
    }
    if (field.showIf === 'resume_condition' && field.key === 'resume_time') {
      return dependsOnValue === 'specific_time';
    }

    return Boolean(dependsOnValue);
  };

  // Render a single field based on its type
  const renderField = (field: ConfigField) => {
    if (!shouldShowField(field)) return null;

    const value = config[field.key] ?? field.default;

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'message_template':
        return (
          <MessageTemplateField
            value={value || ''}
            onChange={(next) => handleFieldChange(field.key, next)}
            placeholder={field.placeholder}
            subject={typeof config.subject === 'string' ? config.subject : null}
            withPreview
          />
        );

      case 'message_subject':
        return (
          <MessageTemplateField
            value={value || ''}
            onChange={(next) => handleFieldChange(field.key, next)}
            placeholder={field.placeholder}
            singleLine
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, parseInt(e.target.value) || 0)}
            min={field.min}
            max={field.max}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleFieldChange(field.key, e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">{field.description || t('workflows.stepConfig.enable', 'Enable')}</span>
          </label>
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('workflows.stepConfig.select', 'Select...')}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {field.options?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(opt.value)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, opt.value]
                      : selectedValues.filter((v: string) => v !== opt.value);
                    handleFieldChange(field.key, newValues);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      case 'page_picker':
        return (
          <NavigationPagePicker
            value={value}
            onChange={(path) => handleFieldChange(field.key, path)}
            placeholder={t('workflows.stepConfig.selectPage', 'Select a page...')}
          />
        );

      case 'template_picker':
        return (
          <TemplatePicker
            value={value}
            onChange={(id) => handleFieldChange(field.key, id)}
          />
        );

      case 'comm_template_picker': {
        // Derive communication method from step type
        const commMethod = stepTypeValue?.includes('email') ? 'email'
          : stepTypeValue?.includes('voice') ? 'voice'
          : 'sms';
        return (
          <TemplateSelector
            communicationMethod={commMethod}
            onTemplateSelect={(template) => {
              handleFieldChange(field.key, template.id);
              // Auto-populate message_body and subject from template
              if (template.body) {
                handleFieldChange('message_body', template.body);
              }
              if (template.subject) {
                handleFieldChange('subject', template.subject);
              }
            }}
            showVariablePreview={true}
            className="w-full"
          />
        );
      }

      case 'duration_picker':
        const hours = value || 24;
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={hours}
              onChange={(e) => handleFieldChange(field.key, parseInt(e.target.value) || 0)}
              min={1}
              max={720}
              className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">{t('workflows.stepConfig.hours', 'hours')}</span>
            <span className="text-xs text-gray-400">
              ({hours >= 24 ? `${Math.floor(hours / 24)} ${t('workflows.stepConfig.days', 'days')} ${hours % 24} ${t('workflows.stepConfig.hrs', 'hrs')}` : `${hours} ${t('workflows.stepConfig.hrs', 'hrs')}`})
            </span>
          </div>
        );

      case 'keyword_mapper':
        const keywords = value || { accept: ['yes', '1'], reject: ['no', '2'] };
        return (
          <div className="space-y-2">
            <div className="text-xs text-gray-500 mb-2">
              {t('workflows.stepConfig.mapKeywordsDescription', 'Map response keywords to workflow actions')}
            </div>
            {Object.entries(keywords).map(([action, words]) => (
              <div key={action} className="flex items-center gap-2">
                <span className="text-sm font-medium capitalize w-20">{action}:</span>
                <input
                  type="text"
                  value={(words as string[]).join(', ')}
                  onChange={(e) => {
                    const newKeywords = {
                      ...keywords,
                      [action]: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    };
                    handleFieldChange(field.key, newKeywords);
                  }}
                  placeholder="keyword1, keyword2"
                  className="flex-1 px-3 py-1 border border-gray-300 rounded-md text-sm"
                />
              </div>
            ))}
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
    }
  };

  // Split fields into basic and advanced
  const basicFieldKeys = [
    'template_id', 'comm_template_id', 'form_template_id', 'message_body',
    'subject', 'description', 'channel', 'billing_type', 'approver_role',
    'voice_agent_id', 'call_script', 'expects_response', 'reminder_enabled',
    'branching_mode', 'condition_field', 'condition_operator', 'condition_value',
    'decision_context', 'true_branch_step', 'false_branch_step',
    'navigation_url',
  ];
  const basicFields = schema.filter((f) => f.required || basicFieldKeys.includes(f.key));
  const advancedFields = schema.filter((f) => !basicFields.includes(f));

  // TRUS-719: a member-message step (program communication / outreach, TCO
  // communication_sms / communication_email) gets inputs for the template's
  // `input` variables. Nothing renders when validate reports none (or fails).
  const memberMessageStep = isMemberMessageStep(stepTypeValue) || isMemberMessageStep(baseType);
  const messageTexts = memberMessageStep ? messageStepTexts(config) : null;
  const variableInputs = messageTexts ? (
    <StepVariableInputs
      subject={messageTexts.subject}
      body={messageTexts.body}
      value={variableInputsOf(config)}
      onChange={(next) => onChange(withVariableInputs(config, next))}
    />
  ) : null;

  // TRUS-686: Post to channel / Notify staff have their own editor.
  const notifyStep = isNotifyStep(stepTypeValue) ? stepTypeValue : isNotifyStep(baseType) ? baseType : null;
  if (notifyStep) {
    return (
      <div className={`step-configuration-panel ${className}`}>
        {stepTypeInfo && (
          <div className="mb-4 flex items-center gap-3 rounded-lg bg-muted p-3">
            {stepTypeInfo.icon}
            <div>
              <div className="font-medium">{stepTypeLabel(t, stepTypeInfo)}</div>
              <div className="text-xs text-muted-foreground">{stepTypeDescription(t, stepTypeInfo)}</div>
            </div>
          </div>
        )}
        <NotifyStepConfig stepType={notifyStep} config={config} onChange={onChange} memberBound={memberBound} />
      </div>
    );
  }

  if (schema.length === 0) {
    return (
      <div className={`step-configuration-panel ${className}`}>
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">{t('workflows.stepConfig.noConfiguration', 'No specific configuration')}</p>
              <p className="text-xs text-amber-600 mt-1">
                {t('workflows.stepConfig.noConfigurationDescription', 'This step type does not have additional configuration options.')}
              </p>
            </div>
          </div>
        </div>
        {variableInputs && <div className="mt-4">{variableInputs}</div>}
      </div>
    );
  }

  return (
    <div className={`step-configuration-panel ${className}`}>
      {/* Step type header */}
      {stepTypeInfo && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
          {stepTypeInfo.icon}
          <div>
            <div className="font-medium text-gray-900">{stepTypeInfo.label}</div>
            <div className="text-xs text-gray-500">{stepTypeInfo.description}</div>
          </div>
        </div>
      )}

      {/* Basic fields */}
      <div className="space-y-4">
        {basicFields.map((field) => {
          const renderedField = renderField(field);
          if (!renderedField) return null;

          return (
            <div key={field.key} className="form-field">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type !== 'boolean' && field.description && (
                <p className="text-xs text-gray-500 mb-2">{field.description}</p>
              )}
              {renderedField}
            </div>
          );
        })}
        {variableInputs}
      </div>

      {/* Advanced fields toggle */}
      {advancedFields.length > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            {showAdvanced ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {t('workflows.stepConfig.advancedOptions', 'Advanced Options')} ({advancedFields.length})
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4">
              {advancedFields.map((field) => {
                const renderedField = renderField(field);
                if (!renderedField) return null;

                return (
                  <div key={field.key} className="form-field">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.type !== 'boolean' && field.description && (
                      <p className="text-xs text-gray-500 mb-2">{field.description}</p>
                    )}
                    {renderedField}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepConfigurationPanel;
